import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { IPaginatedResponse } from "../models/Pagination";
import {
  ICreatePartNumberTransfer,
  IExportSearch,
  IExportTransfer,
  IFilterWarehouse,
  IPartNumberDtoInsert,
  IPartNumberDtoResponse,
  IPartNumberDtoUpdate,
  IPartNumberGroupDtoWithPartNumbers,
  IPartNumberGroupRestock,
  IPartNumberTransfer,
  IPartNumberTransferDetail,
  IPartNumberTransferExport,
  IPartNumberTransferExportResponse,
  IReturnSlipWarehouse,
  IReturnSlipWarehouseDetail,
  IUpdateStatusExportTransfer,
  IWarehouseExport,
  IWarehouseExportResponse,
  IWarehouseGroupDto,
  IWarehouseLocationCreate,
  IWarehouseLocationData,
} from "../models/Warehouse";
import { warehouseService } from "../services/warehouseService";
import {
  IWarehouseExportTransfer,
  IWarehouseLocationDetail,
  IWarehouseLocationUpdate,
} from "./../models/Warehouse";
import { warehouseQueryKey } from "./QueryKey";

export function useWarehouses(
  params: {
    page?: number;
    limit?: number;
    search?: string;
    code?: string;
    location?: string;
    awb?: string;
    partNumber?: string;
    projectCode?: string;
    project?: string;
    stock?: number;
    date?: string;
  } = {},
): UseQueryResult<IPaginatedResponse<IPartNumberDtoResponse>> {
  return useQuery<IPaginatedResponse<IPartNumberDtoResponse>>({
    queryKey: [warehouseQueryKey.WAREHOUSES, params],
    queryFn: () => warehouseService.getWarehouse(params),
  });
}

export function useImportWarehouse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: warehouseService.importWarehouse,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [warehouseQueryKey.WAREHOUSES],
      });
    },
  });
}

export function useInsertWarehouse(): UseMutationResult<
  IPartNumberDtoResponse[] | undefined,
  Error,
  IPartNumberDtoInsert[],
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: IPartNumberDtoInsert[]) =>
      warehouseService.insertWarehouse(data),

    onSuccess: (newData) => {
      queryClient.invalidateQueries({
        queryKey: [warehouseQueryKey.WAREHOUSES],
        predicate: (query) =>
          query.queryKey[0] === warehouseQueryKey.WAREHOUSES,
      });

      queryClient.invalidateQueries({
        queryKey: [warehouseQueryKey.WAREHOUSE_GROUP],
        predicate: (query) =>
          query.queryKey[0] === warehouseQueryKey.WAREHOUSE_GROUP,
      });
    },
  });
}

export function useUpdateWarehouse(
  id: number,
): UseMutationResult<
  IPartNumberDtoResponse,
  Error,
  IPartNumberDtoUpdate,
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: IPartNumberDtoUpdate) =>
      warehouseService.updateWarehouse(id, data),
    onSuccess: (newData) => {
      queryClient.invalidateQueries({
        queryKey: [warehouseQueryKey.WAREHOUSES],
        predicate: (query) =>
          query.queryKey[0] === warehouseQueryKey.WAREHOUSES,
      });
      queryClient.invalidateQueries({
        queryKey: [warehouseQueryKey.WAREHOUSE_GROUP],
      });
    },
  });
}

export function useWarehouseGroupById(
  id: number,
): UseQueryResult<IPartNumberGroupDtoWithPartNumbers> {
  return useQuery({
    queryKey: [warehouseQueryKey.WAREHOUSE_GROUP_DETAIL, id],
    queryFn: () => warehouseService.getWarehouseGroupById(id),
    enabled: !!id,
  });
}

export function useWarehouseGroup(
  params: IFilterWarehouse,
): UseQueryResult<IPaginatedResponse<IWarehouseGroupDto>> {
  return useQuery({
    queryKey: [warehouseQueryKey.WAREHOUSE_GROUP, params],
    queryFn: () => warehouseService.getAllWarehouseGroup(params),
  });
}
export function useRequestQcForWarehouseGroup(): UseMutationResult<
  { message: string },
  Error,
  { id: number; priority: string },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, priority }) =>
      warehouseService.requestQcForWarehouseGroup(id, priority),
    onSuccess: (_, variable) => {
      queryClient.setQueryData(
        [warehouseQueryKey.WAREHOUSE_GROUP_DETAIL, variable.id],
        (oldData: IPartNumberGroupDtoWithPartNumbers) =>
          oldData
            ? { ...oldData, qcRequested: true, priority: variable.priority }
            : oldData,
      );
      queryClient.invalidateQueries({
        queryKey: [warehouseQueryKey.WAREHOUSE_GROUP],
        predicate: (query) =>
          query.queryKey[0] === warehouseQueryKey.WAREHOUSE_GROUP,
      });
    },
  });
}

export function useImportWarehouseExport() {
  return useMutation({
    mutationFn: warehouseService.importExportWarehouse,
  });
}
export function useExportWarehouse(): UseMutationResult<
  IWarehouseExportResponse,
  Error,
  IWarehouseExport,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: IWarehouseExport) =>
      warehouseService.exportWarehouse(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [warehouseQueryKey.WAREHOUSES],
        predicate: (query) =>
          query.queryKey[0] === warehouseQueryKey.WAREHOUSES,
      });
      queryClient.invalidateQueries({
        queryKey: [warehouseQueryKey.WAREHOUSE_EXPORTS],
        predicate: (query) =>
          query.queryKey[0] === warehouseQueryKey.WAREHOUSE_EXPORTS,
      });
    },
  });
}
export function useReverseExportWarehouse(): UseMutationResult<
  { message: string; reversedExportId: number },
  Error,
  { id: number; reason: string },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { id: number; reason: string }) =>
      warehouseService.reverseExportWarehouse(body.id, body.reason),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [warehouseQueryKey.WAREHOUSES],
        predicate: (query) =>
          query.queryKey[0] === warehouseQueryKey.WAREHOUSES,
      });
      queryClient.invalidateQueries({
        queryKey: [warehouseQueryKey.WAREHOUSE_EXPORTS],
        predicate: (query) =>
          query.queryKey[0] === warehouseQueryKey.WAREHOUSE_EXPORTS,
      });
    },
  });
}

export function useWarehouseExports(
  params: IFilterWarehouse,
): UseQueryResult<IPaginatedResponse<IExportSearch>> {
  return useQuery({
    queryKey: [warehouseQueryKey.WAREHOUSE_EXPORTS, params],
    queryFn: () => warehouseService.searchWarehouseExports(params),
    enabled: !!params,
  });
}

export function useGetWarehouseLocationById(
  id: number,
): UseQueryResult<IWarehouseLocationData> {
  return useQuery({
    queryKey: [warehouseQueryKey.WAREHOUSE_LOCATIONS, id],
    queryFn: () => warehouseService.getWarehouseLocationById(id),
    enabled: !!id,
  });
}
export function useCreateWarehouseLocation(): UseMutationResult<
  IWarehouseLocationDetail,
  Error,
  IWarehouseLocationCreate,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: IWarehouseLocationCreate) =>
      warehouseService.createWarehouseLocation(data),
    onSuccess: (newData, variables) => {
      queryClient.setQueryData<IWarehouseLocationData>(
        [warehouseQueryKey.WAREHOUSE_LOCATIONS, variables.warehouseId],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            warehouseLocations: [
              ...(oldData.warehouseLocations || []),
              newData,
            ],
          };
        },
      );
    },
  });
}
export function useEditWarehouseLocation(): UseMutationResult<
  IWarehouseLocationDetail,
  Error,
  IWarehouseLocationUpdate,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: IWarehouseLocationUpdate) =>
      warehouseService.editWarehouseLocation(data),
    onSuccess: (newData, variables) => {
      queryClient.setQueryData<IWarehouseLocationData>(
        [warehouseQueryKey.WAREHOUSE_LOCATIONS, variables.warehouseId],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            warehouseLocations: oldData.warehouseLocations.map((loc) =>
              loc.id === newData.id ? newData : loc,
            ),
          };
        },
      );
    },
  });
}
export function useExportWarehouseTransfer(): UseMutationResult<
  IWarehouseExportResponse,
  Error,
  IWarehouseExportTransfer,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: IWarehouseExportTransfer) =>
      warehouseService.exportWarehouseTransfer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [warehouseQueryKey.WAREHOUSE_EXPORT_TRANSFERS],
        predicate: (query) =>
          query.queryKey[0] === warehouseQueryKey.WAREHOUSE_EXPORT_TRANSFERS,
      });
    },
  });
}
export function useImportWarehouseExportTransfer() {
  return useMutation({
    mutationFn: warehouseService.importExportWarehouseTransfer,
  });
}
export function useGetWarehouseExportTransfer(
  params: {
    page?: number;
    limit?: number;
    search?: string;
    transferId?: number;
  } = {},
): UseQueryResult<IPaginatedResponse<IExportTransfer>> {
  return useQuery<IPaginatedResponse<IExportTransfer>>({
    queryKey: [warehouseQueryKey.WAREHOUSE_EXPORT_TRANSFERS, params],
    queryFn: () => warehouseService.getWarehouseExportTransfer(params),
  });
}
export function useUpdateWarehouseExportTransfer(): UseMutationResult<
  IUpdateStatusExportTransfer,
  Error,
  IUpdateStatusExportTransfer,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: warehouseService.updateWarehouseExportTransfer,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [warehouseQueryKey.WAREHOUSE_EXPORTS],
        predicate: (query) =>
          query.queryKey[0] === warehouseQueryKey.WAREHOUSE_EXPORT_TRANSFERS,
      });
      queryClient.invalidateQueries({
        queryKey: [warehouseQueryKey.WAREHOUSES],
      });
    },
  });
}
export function useTransferWarehouse(): UseMutationResult<
  IPartNumberTransfer,
  unknown,
  ICreatePartNumberTransfer,
  unknown
> {
  return useMutation({
    mutationFn: (data: ICreatePartNumberTransfer) =>
      warehouseService.transferWarehouse(data),
  });
}
export function useGetTransferWarehouse(query: {
  page: number;
  limit: number;
  search?: string;
}): UseQueryResult<IPaginatedResponse<IPartNumberTransfer>> {
  return useQuery({
    queryKey: [warehouseQueryKey.WAREHOUSE_TRANSFERS, query],
    queryFn: () => warehouseService.getTransferWarehouse(query),
    enabled: !!query,
  });
}
export function useGetTransferWarehouseDetail(
  id: number,
): UseQueryResult<IPartNumberTransferDetail> {
  return useQuery({
    queryKey: [warehouseQueryKey.WAREHOUSE_TRANSFER_DETAIL, id],
    queryFn: () => warehouseService.getTransferWarehouseDetail(id),
    enabled: !!id,
  });
}
export function useCompleteTransferWarehouseDetail(): UseMutationResult<
  IPartNumberTransferDetail,
  unknown,
  number,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      warehouseService.completeTransferWarehouseDetail(id),
    onSuccess: (data, variable) => {
      queryClient.setQueryData<IPartNumberTransferDetail>(
        [warehouseQueryKey.WAREHOUSE_TRANSFER_DETAIL, variable],
        (oldData) =>
          oldData ? { ...oldData, isCompleted: data.isCompleted } : oldData,
      );
      queryClient.invalidateQueries({
        queryKey: [warehouseQueryKey.WAREHOUSE_TRANSFERS],
        predicate: (query) =>
          query.queryKey[0] === warehouseQueryKey.WAREHOUSE_TRANSFERS,
      });
    },
  });
}
export function useExportFromTransfer(): UseMutationResult<
  IPartNumberTransferExportResponse,
  unknown,
  { id: number; data: IPartNumberTransferExport },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: IPartNumberTransferExport;
    }) => warehouseService.exportFromTransfer(id, data),
    onSuccess: (data, variable) => {
      queryClient.setQueryData<IPartNumberTransferDetail>(
        [warehouseQueryKey.WAREHOUSE_TRANSFER_DETAIL, data.exportTransferId],
        (oldData) => {
          if (!oldData) return oldData;
          const exportQuantityMap = new Map(
            variable.data.exportTransfers.map((t) => [
              t.partNumberId,
              t.exportQuantity,
            ]),
          );

          return {
            ...oldData,
            isExported: true,
            exportedAt: new Date(),
            partNumberTransferDetails: oldData.partNumberTransferDetails.map(
              (d) => {
                const exportQuantity = exportQuantityMap.get(d.partNumber.id);
                return exportQuantity !== undefined
                  ? { ...d, exportQuantity }
                  : d;
              },
            ),
          };
        },
      );
      queryClient.invalidateQueries({
        queryKey: [warehouseQueryKey.WAREHOUSE_TRANSFERS],
        predicate: (query) =>
          query.queryKey[0] === warehouseQueryKey.WAREHOUSE_TRANSFERS,
      });
    },
  });
}
export function useImportWarehouseTransfer() {
  return useMutation({
    mutationFn: warehouseService.importTransferWarehouse,
  });
}

export function useGetReturnSlipWarehouse(
  params: IFilterWarehouse,
): UseQueryResult<IPaginatedResponse<IReturnSlipWarehouse>> {
  return useQuery({
    queryKey: [warehouseQueryKey.WAREHOUSE_RETURN, params],
    queryFn: () => warehouseService.getReturnSlipWarehouse(params),
    enabled: !!params,
  });
}

export function useGetReturnSlipWarehouseDetail(
  id: number,
): UseQueryResult<IReturnSlipWarehouseDetail> {
  return useQuery({
    queryKey: [warehouseQueryKey.WAREHOUSE_RETURN_DETAIL, id],
    queryFn: () => warehouseService.getReturnSlipWarehouseDetail(id),
    enabled: !!id,
  });
}
export function useAcceptReturnWarehouse(): UseMutationResult<
  IReturnSlipWarehouseDetail,
  unknown,
  { id: number; locationId: number },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { id: number; locationId: number }) =>
      warehouseService.acceptReturnWarehouse(data.id, data.locationId),
    onSuccess: (data, variable) => {
      queryClient.invalidateQueries({
        queryKey: [warehouseQueryKey.WAREHOUSE_RETURN],
        predicate: (query) =>
          query.queryKey[0] === warehouseQueryKey.WAREHOUSE_RETURN,
      });
      queryClient.setQueryData(
        [warehouseQueryKey.WAREHOUSE_RETURN_DETAIL, variable.id],
        data,
      );
    },
  });
}
export function useGetReturnPartNumberByGroup(
  id: number | null,
): UseQueryResult<IPartNumberGroupRestock> {
  return useQuery({
    queryKey: [warehouseQueryKey.WAREHOUSE_GROUP_RESTOCK, id],
    queryFn: () => warehouseService.getReturnPartNumberByGroup(id!),
    enabled: !!id,
  });
}
export function useReturnPartNumbeByGroup(): UseMutationResult<
  number,
  unknown,
  Record<number, number | null>,
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ngLocationsByItem) =>
      warehouseService.returnPartNumberByGroup(ngLocationsByItem),
    onSuccess: (groupId, ngLocationsByItem) => {
      const restockIds = Object.keys(ngLocationsByItem).map(Number);
      queryClient.setQueryData(
        [warehouseQueryKey.WAREHOUSE_GROUP_RESTOCK, groupId],
        (oldData: IPartNumberGroupRestock | undefined) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            partNumberRestocks: oldData.partNumberRestocks.map((p) =>
              restockIds.includes(p.id)
                ? {
                    ...p,
                    status: "RETURNED",
                    returnedAt: new Date(),
                  }
                : p,
            ),
          };
        },
      );
      queryClient.invalidateQueries({
        queryKey: [warehouseQueryKey.WAREHOUSE_GROUP_DETAIL, groupId],
        exact: true,
      });
    },
  });
}
